import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Subscription } from 'rxjs';
import { catchError, concatMap, tap } from 'rxjs/operators';

import { BaseSortableSearchComponent } from '@components/_base/base-search/base-sortable-search.component';
import { displayError } from '@app/_utils/error-util';
import { prepareDateForDb } from '@app/_utils/date-util';
import { UIEventCustom } from '@app/_utils/ui-event-util';
import { NomenclatureService } from '@app/_services/nomenclature.service';
import { PaymentType } from '@app/_models/payment-type.model';

import {
  AccountingDocumentForMatch,
  BankStatementMatch,
  BANK_PAYMENT_TYPE_IDS,
  MatchDialogData,
  MatchDialogResult
} from '../_models/bank-statement-match.model';
import { BankStatementService } from '../_services/bank-statement.service';

/**
 * Links a bank statement to the invoices it settles, by hand.
 *
 * This is where the statements the nightly matcher could not resolve get dealt with. Linking
 * an invoice moves it to paid; removing the link moves it back, unless another statement is
 * still paying it.
 *
 * Both directions of the relation happen in practice - one transfer covering several invoices,
 * and several transfers covering one - so the dialog warns about amounts that do not add up
 * rather than refusing them. Deciding whether an odd-looking allocation is right is the
 * operator's job; this only makes sure they can see it.
 */
@Component({
  selector: 'app-match-bank-statement-dialog',
  templateUrl: './match-bank-statement-dialog.component.html',
  styleUrls: ['./match-bank-statement-dialog.component.css']
})
export class MatchBankStatementDialogComponent extends BaseSortableSearchComponent<AccountingDocumentForMatch>
  implements OnInit, OnDestroy {

  paymentTypes: PaymentType[] = [];
  matches: BankStatementMatch[] = [];

  /** Nothing is linked or unlinked until the current call comes back. */
  working = false;

  /** True once the operator has actually searched - the table is hidden before that. */
  searched = false;

  /** Set when anything was linked or unlinked, so the list screen knows to refresh. */
  private changed = false;

  searchForm = this.fb.group({
    invoiceNumber:     [null],
    recipientFullName: [null],
    // Pre-set to the types a bank transfer settles; the operator can widen or clear it.
    paymentTypeIds:    [BANK_PAYMENT_TYPE_IDS],
    dateFrom:          [null],
    dateTo:            [null],
    totalAmount:       [null]
  });

  searchSubscription: Subscription;

  search$ = this.searchSubject.asObservable().pipe(
    tap(() => this.dataLoaded = false),
    concatMap(() =>
      this.bankStatementService.searchDocuments(
        this.sortingPaging,
        this.searchForm.value,
        prepareDateForDb(this.searchForm.get('dateFrom')?.value),
        prepareDateForDb(this.searchForm.get('dateTo')?.value)).pipe(
          tap(([page, totalCount]) => {
            this.sortingPaging.fromRow = page.fromRow;
            this.sortingPaging.toRow = page.toRow;
            this.sortingPaging.totalElements = totalCount;
            this.content = page.content;
            this.dataLoaded = true;
          })
        )
    ),
    catchError(err => {
      displayError(err);
      this.dataLoaded = false;
      this.errorMessageSubject.next(this.translateService.instant('messages.errorLoadingData'));
      return EMPTY;
    })
  );

  constructor(
    private fb:                   FormBuilder,
    private dialogRef:            MatDialogRef<MatchBankStatementDialogComponent, MatchDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: MatchDialogData,
    private bankStatementService: BankStatementService,
    private nomenclatureService:  NomenclatureService,
    private uiEvent:              UIEventCustom,
    private translateService:     TranslateService,
  ) {
    super();
  }

  ngOnInit() {
    this.searchSubscription = this.search$.subscribe();

    // Deliberately NOT firing an initial search, unlike every other list screen here: the
    // invoice table stays empty until the operator narrows it down to what they are after.
    this.loadPaymentTypes();
    this.loadMatches();
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
  }

  search() {
    this.searched = true;
    super.search();
  }

  clearSearch() {
    this.searchForm.reset({ paymentTypeIds: BANK_PAYMENT_TYPE_IDS });
    this.content = [];
    this.searched = false;
  }

  /** Links the invoice, then reloads both panels so no button is left showing a stale state. */
  link(document: AccountingDocumentForMatch) {
    if (this.working) {
      return;
    }
    this.working = true;

    this.bankStatementService.link(this.data.statementId, document.id).subscribe({
      next: () => {
        this.working = false;
        this.changed = true;
        this.uiEvent.displayUISuccess();
        this.loadMatches();
        this.searchSubject.next();
      },
      error: err => {
        this.working = false;
        displayError(err);
        this.uiEvent.displayUIError();
      }
    });
  }

  unlink(match: BankStatementMatch) {
    if (this.working) {
      return;
    }
    this.working = true;

    this.bankStatementService.unlink(match.id).subscribe({
      next: () => {
        this.working = false;
        this.changed = true;
        this.uiEvent.displayUISuccess();
        this.loadMatches();
        if (this.searched) {
          this.searchSubject.next();
        }
      },
      error: err => {
        this.working = false;
        displayError(err);
        this.uiEvent.displayUIError();
      }
    });
  }

  /** Whether this invoice is already linked - the + button turns into a tick. */
  isLinked(document: AccountingDocumentForMatch): boolean {
    return this.matches.some(match => match.accountingDocumentId === document.id
      || match.invoiceNumber === document.invoiceNumber);
  }

  /** What the linked invoices come to in total. */
  get linkedTotal(): number {
    return this.matches.reduce((sum, match) => sum + (match.allocatedAmount || 0), 0);
  }

  /**
   * Whether the linked invoices account for exactly the transfer. Not an error either way -
   * a transfer can legitimately be a part payment - but the operator should see it.
   */
  get totalsAgree(): boolean {
    return this.matches.length > 0 && Math.abs(this.linkedTotal - (this.data.amount || 0)) < 0.005;
  }

  /** Invoices receiving more across all statements than they are worth. */
  get overAllocated(): BankStatementMatch[] {
    return this.matches.filter(match => match.invoiceTotalAmount != null
      && match.totalAllocatedToInvoice != null
      && match.totalAllocatedToInvoice - match.invoiceTotalAmount > 0.005);
  }

  close() {
    this.dialogRef.close({ changed: this.changed });
  }

  private loadMatches() {
    this.bankStatementService.findMatches(this.data.statementId).subscribe({
      next: matches => this.matches = matches,
      error: err => {
        displayError(err);
        this.uiEvent.displayUIError();
      }
    });
  }

  private loadPaymentTypes() {
    this.nomenclatureService.getPaymentTypes().subscribe({
      next: paymentTypes => this.paymentTypes = paymentTypes,
      error: err => displayError(err)
    });
  }
}
